import { MockEntityObjectCreator } from "@/testing";
import { IGameOrganizer } from "@/types/interfaces";
import { Game, RoomName } from "@/types/types";
import { logError } from "@lib/logger";

class GameOrganizer implements IGameOrganizer {
	public getGameById(gameId: string, allGames: Game[]): Game | null {
		const game = allGames.find(g => g.id === gameId);
		if (!game) {
			throw new Error(`Game with ID ${gameId} not found.`);
		}
		return game;
	}

	public replaceGameInOrder(
		newGameId: string | null,
		index: number,
		order: Map<number, string | null> | Record<string, string | null>
	): Map<number, string | null> {

		const orderMap = order instanceof Map
			? new Map<number, string | null>(order)
			: new Map<number, string | null>(
				Object.entries(order ?? {}).map(([k, v]) => [Number(k), v as string | null])
			);

		const idx = Number(index);
		if (!Number.isInteger(idx) || idx < 0) {
			throw new Error("index must be a non-negative integer");
		}

		orderMap.set(idx, newGameId);

		return orderMap;
	}

	public createGameListFromOrder(
		gameOrder: Map<number, string | null> | Record<string, string | null>,
		allGames: Game[]
	): Game[] {
		const orderMap = this._deserializeGameOrder(gameOrder);

		const result: Game[] = [];

		orderMap.forEach((id) => {
			if (!id) return; // skip null placeholders
			const game = allGames.find(g => g.id === id);
			if (game) {
				result.push(game);
			}
		});

		return result;
	}


	public returnGamesInOrder(
		gameOrder: Map<number, string | null> | Record<string, string | null>,
		gamesToBePlayed: Game[]
	): Game[] {

		gameOrder = this._deserializeGameOrder(gameOrder);
		gameOrder = this.removeGamesNotInGamesToBePlayed(gameOrder, gamesToBePlayed);

		if (gameOrder.size === 0 && gamesToBePlayed.length > 0) {
			gameOrder = this.createDefaultGameOrder(gamesToBePlayed);
		}

		this.validateGameOrder(gameOrder, gamesToBePlayed);

		const mockEntityCreator = new MockEntityObjectCreator();

		return Array.from(gameOrder.entries()).map(([i, id]) => {
			if (id) {
				const game = gamesToBePlayed.find(g => g.id === id);
				if (!game) {
					throw new Error(
						"The game order contains a game ID that is not in the list of games to be played."
					);
				}
				return game;
			}
			return mockEntityCreator.createEmptyGameForRoom(this.getRoomForIndex(i));
		});
	}

	/**
	 * Group games by room for display purposes.
	 * Returns games in the order: Skills games, Brain games, Action games.
	 * Each group maintains its internal order from the original list.
	 */
	public groupGamesByRoom(games: Game[]): Game[] {
		const skillsGames: Game[] = [];
		const brainGames: Game[] = [];
		const actionGames: Game[] = [];

		games.forEach(game => {
			switch (game.default_room) {
				case RoomName.skills:
					skillsGames.push(game);
					break;
				case RoomName.brain:
					brainGames.push(game);
					break;
				case RoomName.action:
					actionGames.push(game);
					break;
			}
		});

		return [...skillsGames, ...brainGames, ...actionGames];
	}

	/**
	 * Group games by room and return them as sections with room labels.
	 * Useful for rendering grouped sections in UI.
	 */
	public getGamesSectioned(games: Game[]): { room: RoomName; games: Game[] }[] {
		const skillsGames: Game[] = [];
		const brainGames: Game[] = [];
		const actionGames: Game[] = [];

		games.forEach(game => {
			switch (game.default_room) {
				case RoomName.skills:
					skillsGames.push(game);
					break;
				case RoomName.brain:
					brainGames.push(game);
					break;
				case RoomName.action:
					actionGames.push(game);
					break;
			}
		});

		const sections: { room: RoomName; games: Game[] }[] = [];

		if (skillsGames.length > 0) {
			sections.push({ room: RoomName.skills, games: skillsGames });
		}
		if (brainGames.length > 0) {
			sections.push({ room: RoomName.brain, games: brainGames });
		}
		if (actionGames.length > 0) {
			sections.push({ room: RoomName.action, games: actionGames });
		}

		return sections;
	}

	private _deserializeGameOrder(
		gameOrder: Map<number, string | null> | Record<string, string | null>
	): Map<number, string | null> {
		if (gameOrder instanceof Map) {
			return gameOrder;
		}

		return new Map<number, string | null>(
			Object.entries(gameOrder ?? {}).map(([k, v]) => [Number(k), v])
		);
	}

	public removeGamesNotInGamesToBePlayed(
		gameOrder: Map<number, string | null> | Record<string, string | null>,
		gamesToBePlayed: Game[]
	): Map<number, string | null> {
		gameOrder = this._deserializeGameOrder(gameOrder);
		const gamesToBePlayedIds = new Set(gamesToBePlayed.map(game => game.id));
		const cleanedGameOrder = new Map<number, string | null>();

		gameOrder.forEach((gameId, position) => {
			if (gameId !== null && !gamesToBePlayedIds.has(gameId)) {
				logError(`Game ID ${gameId} in game order is not in games to be played, treating as empty slot`);
				cleanedGameOrder.set(position, null);
				return;
			}
			cleanedGameOrder.set(position, gameId);
		});

		return cleanedGameOrder;
	}

	public validateGameOrder(gameOrder: Map<number, string>, gamesToBePlayed: Game[]): boolean {
		if (gameOrder.size === 0 && gamesToBePlayed.length === 0) {
			return true;
		}

		const gameIdToRoom = this._mapGameIdsToRooms(gamesToBePlayed);
		let expectedRoomIndex = 0;
		let counter = 0;

		while (counter < gameOrder.size) {
			const gameId = gameOrder.get(counter);

			if (gameId === null) {
				counter++;
				expectedRoomIndex = (expectedRoomIndex + 1) % 3;
				continue;
			}

			const roomNameForGameInOrder = gameIdToRoom.get(gameId);
			if (!roomNameForGameInOrder) {
				throw new Error(`Game ID ${gameId} not found in games to be played.`);
			}

			const currentRoomIndex = roomNameForGameInOrder === RoomName.skills ? 0 :
				roomNameForGameInOrder === RoomName.brain ? 1 :
					roomNameForGameInOrder === RoomName.action ? 2 : null;

			if (currentRoomIndex === expectedRoomIndex) {
				expectedRoomIndex = (expectedRoomIndex + 1) % 3;
			} else {
				throw new Error("Game order is incorrect.");
			}
			counter++;
		}
		return true;
	}

	public createDefaultGameOrder(gamesToBePlayed: Game[]): Map<number, string> {
		const gameOrder = new Map<number, string>();

		if (gamesToBePlayed.length === 0) {
			return gameOrder;
		}

		const remainingGames = [...gamesToBePlayed];
		let position = 0;
		let cycleIndex = 0; // 0=skills, 1=brain, 2=action

		while (remainingGames.length > 0) {
			const expectedRoomType = cycleIndex === 0 ? RoomName.skills :
				cycleIndex === 1 ? RoomName.brain :
					RoomName.action;

			let foundIndex = -1;

			for (let i = 0; i < remainingGames.length; i++) {
				const game = remainingGames[i];

				if (game === null) {
					foundIndex = i;
					break;
				} else if (game.default_room === expectedRoomType) {
					foundIndex = i;
					break;
				}
			}

			if (foundIndex !== -1) {
				const selectedGame = remainingGames[foundIndex];
				gameOrder.set(position, selectedGame === null ? null : selectedGame.id);
				remainingGames.splice(foundIndex, 1);
			} else {
				gameOrder.set(position, null);
			}

			position++;
			cycleIndex = (cycleIndex + 1) % 3;
		}

		return gameOrder;
	}

	public changeGamePosition(game: Game, newPosition: number, gameOrder: Map<number, string>, gamesToBePlayed: Game[]): Map<number, string> {
		this.validateGameOrder(gameOrder, gamesToBePlayed);

		const newOrder = this._createOrderWithMovedGame(gameOrder, game, newPosition);

		this.validateGameOrder(newOrder, gamesToBePlayed);

		return newOrder;
	}

	public swapGames(game1: Game, game2: Game, gameOrder: Map<number, string>, gamesToBePlayed: Game[]): Map<number, string> {
		this.validateGameOrder(gameOrder, gamesToBePlayed);
		const newOrder = this._createOrderWithSwappedGames(gameOrder, game1, game2);

		this.validateGameOrder(newOrder, gamesToBePlayed);
		return newOrder;
	}

	private getRoomForIndex(index: number): RoomName {
		const rooms = [RoomName.skills, RoomName.brain, RoomName.action];
		return rooms[index % rooms.length];
	}

	private _mapGameIdsToRooms(gamesToBePlayed: Game[]): Map<string, RoomName> {
		const gameIdToRoom = new Map<string, RoomName>();
		for (const game of gamesToBePlayed) {
			if (game !== null) {
				gameIdToRoom.set(game.id, game.default_room as RoomName);
			}
		}
		return gameIdToRoom;
	}

	private _getMaxIndexFromGameOrder(newOrder: Map<number, string>): number {
		return Math.max(...Array.from(newOrder.keys()));
	}

	private _fillLastMissingIndicesWithNull(gameOrder: Map<number, string>, upToIndex: number): Map<number, string> {
		const newOrder = new Map(gameOrder);
		const maxExistingIndex = this._getMaxIndexFromGameOrder(newOrder);
		const targetIndex = Math.max(upToIndex, maxExistingIndex);

		for (let i = 0; i <= targetIndex; i++) {
			if (!newOrder.has(i)) {
				newOrder.set(i, null);
			}
		}

		return newOrder;
	}

	private _createOrderWithMovedGame(gameOrder: Map<number, string>, game: Game, newPosition: number): Map<number, string> {
		let newOrder = this._removeGameFromCurrentPosition(gameOrder, this._findGamePosition(gameOrder, game));
		newOrder = this._placeGameAtNewPosition(newOrder, game, newPosition);
		return newOrder;
	}

	private _removeGameFromCurrentPosition(gameOrder: Map<number, string>, position: number): Map<number, string> {
		const newOrder = new Map(gameOrder);
		newOrder.set(position, null);
		return newOrder;
	}

	private _placeGameAtNewPosition(gameOrder: Map<number, string>, game: Game, newPosition: number): Map<number, string> {
		const filledOrder = this._fillLastMissingIndicesWithNull(gameOrder, newPosition);
		filledOrder.set(newPosition, game.id);
		return filledOrder;
	}

	private _createOrderWithSwappedGames(gameOrder: Map<number, string>, game1: Game, game2: Game) {
		const newOrder = new Map(gameOrder);
		const position1 = this._findGamePosition(newOrder, game1);
		const position2 = this._findGamePosition(newOrder, game2);

		newOrder.set(position1, game2.id);
		newOrder.set(position2, game1.id);
		return newOrder;
	}

	private _findGamePosition(gameOrder: Map<number, string>, game: Game): number {
		for (const [position, gameId] of Array.from(gameOrder)) {
			if (gameId === game.id) {
				return position;
			}
		}

		throw new Error(`Game with ID ${game.id} not found in game order.`);
	}
}

export default GameOrganizer;